import React, { useState, useEffect } from "react";
import {
  Box as MuiBox,
  Grid,
  TextField as MuiTextField,
  Typography,
  CircularProgress
} from "@mui/material";
import styled from "@emotion/styled";
import axios from "axios";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import CustomButton from "../../../components/CustomButton/Button";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CopyToClipboardWrapper from "../../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailChangeSection from "./components/EmailChangeSection";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const Box = styled(MuiBox)`
  display: flex;
  gap: 28px;
  margin-top: 20px;
`;

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }
  label.additional-class {
    position: none !important;
    left: 0 !important;
    top: 0 !important;
    // font-weight: bold !important;
    // color: red !important;
  }
  additional-class-input {
    padding: 24px 0 5px !important;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

interface UserProfileProps {
  userData: UserData | any;
  setUserDetail: (data: UserData) => void;
  userDefaultData: UserData | any;
  fetchUserData: () => void;
  loading: boolean;
  setNotification: (notification: any) => void;
}

interface UserData {
  UserId: string;
  CreationDate: string;
  LastLoginDate: string;
  Email: string;
  FirstName: string;
  SurName: string;
  DateOfBirth: string;
  Mobile: string;
  AccountCode: string;
  AssociatedAccountCodes: string[];
  InitialSignInProvider: string;
  ExistingSignInProviders: string[];
  CurrentSignInProvider: string;
  SocialHandles: {
    platform: string;
    handle: string;
  }[];
  Addresses: Array<{
    AddressLabel: string;
    Address: {
      Suburb: string;
      State: string;
      Street: string;
      RecordId: string;
      UnitNumber: string;
      PostCode: string;
      AddressType: string;
    };
  }>;
  IsActive: number;
}

const UserProfilePage: React.FC<UserProfileProps> = ({
  userData,
  setUserDetail,
  userDefaultData,
  fetchUserData,
  loading,
  setNotification = () => {}
}) => {
  const { userId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(
    userData?.DateOfBirth ? new Date(userData.DateOfBirth) : null
  );

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  // Sync local dateOfBirth state when userData updates (after fetch)
  useEffect(() => {
    const dob = userData?.DateOfBirth;
    if (!dob) {
      setDateOfBirth(null);
      return;
    }
    // Normalize "YYYY-MM-DD HH:mm:ss" -> ISO-like by inserting 'T'
    const normalizedDob = dob.includes("T") ? dob : dob.replace(" ", "T");
    const parsed = new Date(normalizedDob);
    if (!isNaN(parsed.getTime())) {
      setDateOfBirth(parsed);
    } else {
      setDateOfBirth(null);
    }
  }, [userData?.DateOfBirth]);

  const handleSave = async () => {
    if (!userId) return;
    try {
      setSaveLoading(true);
      setNotification({ type: null, message: null });
      // Email is deliberately absent: the sign-in address is only ever changed
      // through the recovery flow, never by this profile save.
      const dataToSave = {
        UserId: userId,
        FirstName: userData.FirstName,
        SurName: userData.SurName,
        DateOfBirth: dateOfBirth?.toISOString(),
        IsActive: userData?.IsActive
      };
      await axios.put(`${apiBaseUrl}/users/support/user`, dataToSave, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      });
      await fetchUserData();
      setIsEditing(false);
      setNotification({
        type: "success",
        message: "User profile updated successfully"
      });
    } catch (err: any) {
      console.error("Failed to update user data:", err);
      setNotification({
        type: "error",
        message: "Failed to update user profile"
      });
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography>No user data available</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <div style={{ float: "right", cursor: "pointer" }}>
          <RefreshIcon
            onClick={() => {
              setNotification({
                type: null,
                message: null
              });
              fetchUserData();
            }}
          />
        </div>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-userid-input"
            label="User ID"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true,
              endAdornment: (
                <CopyToClipboardWrapper
                  text={userData?.UserId || ""}
                  cell="UserId"
                />
              )
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={userData?.UserId?.split("-")?.pop() || ""}
            fullWidth
            disabled={true}
          />

          <TextField
            id="standard-creation-date-input"
            label="Creation Date"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={
              userData.CreationDate
                ? getFormattedDate(userData.CreationDate)
                : ""
            }
            disabled={true}
          />
          <TextField
            id="standard-last-login-input"
            label="Last Login Date"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={
              userData.LastLoginDate
                ? getFormattedDate(userData.LastLoginDate)
                : ""
            }
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          {/* Read-only: the sign-in email is only changed through the
              recovery flow below, never by editing it in place. */}
          <TextField
            id="standard-email-input"
            label="Email"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="email"
            variant="standard"
            sx={{ height: "55px" }}
            value={userData.Email || ""}
            fullWidth
            disabled={true}
          />
          <TextField
            id="standard-firstname-input"
            label="First Name"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input"
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={userData.FirstName}
            fullWidth
            onChange={(e) =>
              setUserDetail({ ...userData, FirstName: e.target.value })
            }
          />

          <TextField
            id="standard-surname-input"
            label="Surname"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input"
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={userData.SurName}
            fullWidth
            onChange={(e) =>
              setUserDetail({ ...userData, SurName: e.target.value })
            }
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <DatePicker
            selected={dateOfBirth}
            disabled={true}
            onChange={(date) => {
              setDateOfBirth(date);
              if (isEditing) {
                setUserDetail({
                  ...userData,
                  DateOfBirth: date?.toISOString()
                });
              }
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText="Date of Birth"
            customInput={
              <TextField
                id="standard-dateofbirth-input"
                label="Date of Birth"
                InputLabelProps={{ className: "additional-class" }}
                InputProps={{
                  className: "additional-class-input"
                }}
                type="text"
                variant="standard"
                sx={{ width: "285px", height: "55px" }}
                fullWidth
                disabled={true}
              />
            }
          />

          <TextField
            id="standard-mobile-input"
            label="Mobile"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={userData.Mobile || ""}
            disabled={true}
          />
          <TextField
            id="standard-accountcode-input"
            label="Account Code"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={userData.AccountCode || ""}
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-initialsigninprovider-input"
            label="Initial Sign In Provider"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={userData.InitialSignInProvider || ""}
            disabled={true}
          />

          <TextField
            id="standard-existingsigninproviders-input"
            label="Existing Sign In Providers"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={userData.ExistingSignInProviders?.join(", ") || ""}
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-currentsigninprovider-input"
            label="Current Sign In Provider"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ width: "400px", height: "55px" }}
            value={userData.CurrentSignInProvider || ""}
            disabled={true}
          />

          {userData.SocialHandles &&
            typeof userData.SocialHandles === "object" &&
            !Array.isArray(userData.SocialHandles) &&
            Object.entries(userData.SocialHandles).map(([key, value]) => (
              <TextField
                key={key}
                id={`standard-socialhandle-${key}-input`}
                label={key}
                InputLabelProps={{ className: "additional-class" }}
                InputProps={{
                  className: "additional-class-input",
                  readOnly: true
                }}
                type="text"
                variant="standard"
                sx={{ height: "55px" }}
                value={value || ""}
                fullWidth
                disabled={true}
              />
            ))}
        </Box>

        {userData?.Addresses?.map((addressData: any, index: number) => (
          <Box key={index} display="flex" flexDirection="row" gap={3}>
            <TextField
              id={`standard-address-${index}-input`}
              label={`Address (${addressData.AddressLabel})`}
              InputLabelProps={{ className: "additional-class" }}
              InputProps={{
                className: "additional-class-input",
                readOnly: true
              }}
              type="text"
              variant="standard"
              sx={{ height: "55px" }}
              value={`${addressData.Address.Street || ""}${
                addressData.Address.UnitNumber
                  ? `, Unit ${addressData.Address.UnitNumber || ""}`
                  : ""
              } ${addressData.Address.Suburb || ""} ${
                addressData.Address.State || ""
              } ${addressData.Address.PostCode || ""}`}
              fullWidth
              disabled={true}
            />
          </Box>
        ))}

        <Box display="flex" flexDirection="row" gap={3}>
          <CustomButton
            callBack={handleSave}
            disabled={_.isEqual(userData, userDefaultData) || saveLoading}
            styles={{ minHeight: "36px" }}
          >
            {saveLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save"
            )}
          </CustomButton>
        </Box>

        {userId && (
          <EmailChangeSection
            userId={userId}
            currentEmail={userDefaultData?.Email || userData?.Email || ""}
            onEmailChanged={fetchUserData}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default UserProfilePage;
